import { useMobileBoxLayout } from './BoxLayouts';
import { SearchIcon } from '@src/components/icons/SearchIcon';
import { Link as InlineLink } from '@src/components/Link/Link';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, CardInfo } from '@lub0v/santa-utils';
import { Button, Checkbox, Cluster, ParticipantFace, Row, Stack, Switch, Typography, CloseIcon, Hint, Input, Pagination, showDialog, HintError } from '@src/components';
import { useAppSelector } from '@src/state/hooks';
import { useOrganizerBoxExclusions } from './useOrganizerBoxExclusions';
import { ChevronDownIcon } from '@src/components/icons/ChevronDownIcon';
import { PencilIcon } from '@src/components/icons/PencilIcon';
import './GameExclusionsDialog.css';
import { isDrawImpossible } from '@src/pages/RandomizerPage/draft';
import { santaClient } from '@src/state/client';

function GameExclusions({ info: initialInfo, cards: initialCards, onChanged }: {
  info: BoxInfo; cards: CardInfo[]; onChanged: () => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const loaded = useAppSelector(state => state.box);
  const info = loaded.info?.box.key === initialInfo.box.key ? loaded.info : initialInfo;
  const cards = loaded.info?.box.key === initialInfo.box.key ? loaded.cards : initialCards;
  const refreshCards = onChanged;
  const edit = useOrganizerBoxExclusions(info, cards, refreshCards);
  const [revealed, setRevealed] = useState<Set<number>>(new Set());
  const [onlyWithExclusions, setOnlyWithExclusions] = useState(() => {
    const participantIds = new Set(initialCards.map(card => card.id));
    return initialCards.some(card => card.exclusions?.some(id => id !== card.id && participantIds.has(id)));
  });
  const currentExclusions = (card: CardInfo) => cards.filter(person => person.id !== card.id && card.exclusions?.includes(person.id));
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const searchable = cards.length > 10;
  const normalizeSearch = (value: string) => value.toLocaleLowerCase().replace(/ё/g, 'е');
  const searchTerms = normalizeSearch(searchable ? query : '').trim().split(/\s+/).filter(Boolean);
  const filteredCards = cards.filter(card =>
    searchTerms.every(term => normalizeSearch(card.username || '').includes(term)) &&
    (!onlyWithExclusions || currentExclusions(card).length > 0));
  const pageCount = Math.max(1, Math.ceil(filteredCards.length / 10));
  const currentPage = Math.min(page, pageCount);
  const visibleCards = filteredCards.slice((currentPage - 1) * 10, currentPage * 10);
  const allOpen = visibleCards.length > 0 && visibleCards.every(card => revealed.has(card.id));
  const canEdit = info.isAdmin && !info.isActiveDraw && !info.box.isArchived;
  const mobile = useMobileBoxLayout();
  const [listRequested, setListRequested] = useState(false);
  const hasExclusions = cards.some(card => currentExclusions(card).length > 0);
  const showList = mobile || cards.length <= 10 || hasExclusions || listRequested || !canEdit;
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  async function saveExclusions(card: CardInfo, nextIds: number[]) {
    if (!canEdit || savingRef.current) return;
    const draft = {
      gameName: info.box.name,
      names: cards.map(person => String(person.id)),
      exclusionsByName: Object.fromEntries(cards.map(person => [String(person.id),
        (person.id === card.id ? nextIds : currentExclusions(person).map(recipient => recipient.id)).map(String)])),
    };
    if (isDrawImpossible(draft, Boolean(info.box.useCircleDraw)) && nextIds.some(id => !card.exclusions?.includes(id))) {
      setSaveError('randomizer.impossible');
      return;
    }
    setSaveError(null);
    savingRef.current = true;
    setSaving(true);
    try {
      const result = await santaClient.setBoxCardExclusions({
        key: info.box.key,
        cardId: card.id,
        cardIds: nextIds,
      });
      if (!result.ok) throw new Error(result.error?.message || 'newBox.saveFailed');
      await refreshCards();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'newBox.saveFailed');
      await refreshCards();
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }
  const description = <Typography as="p" role={mobile ? "meta/helper" : "body/sm"} color="inherit">{t(mobile ? 'gameExclusions.description' : 'organizerPanel.exclusionsDescription')}{' '}
      <InlineLink variant="inline" testId="organizer-exclusions-help" onClick={() => showDialog({
        title: t('organizerPanel.exclusionsHelpTitle'),
        testId: 'organizer-exclusions-help-dialog',
        closeLabel: t('dialog.close'),
        submit: t('game.draw.circleHelp.close'),
        children: <Stack gap={4}>
          <Typography as="p" role="body/base">{t('organizerPanel.exclusionsHelpIntro')}</Typography>
          <Typography as="p" role="body/base">{t('organizerPanel.exclusionsHelpExamples')}</Typography>
          <Typography as="p" role="body/base">{t('organizerPanel.exclusionsHelpDirection')}</Typography>
          <Hint tone="info">{t('organizerPanel.exclusionsHelpLimit')}</Hint>
        </Stack>,
      })}>{t('organizerPanel.learnMore')}</InlineLink>
    </Typography>;
  return <Stack gap={4}>
    <HintError error={saveError} testId="organizer-exclusions-save-error" />
    {info.isActiveDraw && <Hint tone="info" testId="organizer-exclusions-readonly">{t('organizerPanel.exclusionsLockedAfterDraw')}</Hint>}
    {mobile ? description : <Hint tone="info" testId="organizer-exclusions-description">{description}</Hint>}
    {!showList ? <Button variant="secondary" fullWidth onClick={() => setListRequested(true)} testId="organizer-exclusions-configure">{t('organizerPanel.configureExclusions')}</Button> : <>
    {searchable && <Input type="search" name="organizer-exclusions-search"
      aria-label={t('organizerPanel.exclusionsSearch')}
      placeholder={t('organizerPanel.exclusionsSearch')}
      value={query} onValueChange={value => { setQuery(value); setPage(1); }} onClear={() => { setQuery(''); setPage(1); }}
      clearLabel={t('organizerPanel.exclusionsClearSearch')} iconStart={<SearchIcon />}
      autoComplete="off" testId="organizer-exclusions-search" />}
    <Cluster justify="between" wide className="game-exclusions-toolbar organizer-inline-exclusions__toolbar">
      <div><Checkbox checked={onlyWithExclusions} onCheckedChange={value => { setOnlyWithExclusions(value); setPage(1); }} label={t(mobile ? 'gameExclusions.onlyWithExclusions' : 'organizerPanel.showOnlyWithExclusions')} /></div>
      <button type="button" className="link link--inline" disabled={!visibleCards.length}
        onClick={() => setRevealed(current => { const next = new Set(current); visibleCards.forEach(card => { if (allOpen) next.delete(card.id); else next.add(card.id); }); return next; })}
        data-testid="game-exclusions-expand-all">{t(allOpen ? 'gameExclusions.collapseAll' : 'gameExclusions.expandAll')}</button>
    </Cluster>
    {visibleCards.length === 0 && <Typography role="body/base">{t(searchTerms.length ? 'organizerPanel.exclusionsSearchEmpty' : onlyWithExclusions ? 'gameExclusions.noMatches' : 'game.people.noParticipants')}</Typography>}
    <div className="organizer-inline-exclusions__rows">{visibleCards.map((card, index) => {
      const visible = revealed.has(card.id);
      const exclusions = visible ? currentExclusions(card) : [];
      const contentId = `game-exclusions-content-${card.id}`;
      const toggle = () => setRevealed(current => {
        const next = new Set(current); if (next.has(card.id)) next.delete(card.id); else next.add(card.id); return next;
      });
      return <Row as="div" key={card.id} testId={`game-exclusions-${card.id}`}>
        <button type="button" className="game-exclusions-row organizer-inline-exclusions__toggle"
          aria-expanded={visible} aria-controls={contentId}
          aria-label={`${t(visible ? 'gameExclusions.collapse' : 'gameExclusions.show')}: ${card.username}`}
          onClick={toggle}>
          <ParticipantFace card={card} index={index} size="row" />
          <span className="organizer-inline-exclusions__identity">
            <Typography as="span" role="body/strong">{card.username}</Typography>
            <Typography as="span" role="meta/helper">{visible ? t('gameExclusions.count', { count: exclusions.length }) : t('gameExclusions.hidden')}{!visible && <> · <span className="organizer-inline-exclusions__show">{t('gameExclusions.show')}</span></>}</Typography>
          </span>
          <ChevronDownIcon className={visible ? 'game-exclusions-chevron--open' : undefined} />
        </button>
        {visible && <div className="game-exclusions-details" id={contentId}>
          <Stack gap={2}>
            <Cluster justify="between" wide>
              <Typography as="p" role="meta/helper">{t('gameExclusions.cannotGive')}</Typography>
              {canEdit && <Button variant="ghost" size="small" icon={<PencilIcon />} disabled={saving} onClick={() => edit(card, true)}>{t('organizerPanel.editExclusionsShort')}</Button>}
            </Cluster>
            {exclusions.length ? <ul className="game-exclusions-recipients">{exclusions.map(person => (
              <li key={person.id} className="game-exclusions-recipient" data-testid={`game-exclusion-${card.id}-${person.id}`}>
                <ParticipantFace card={person} index={cards.findIndex(candidate => candidate.id === person.id)} size="row" />
                <Typography role="body/strong" className="game-exclusions-recipient__name">{person.username}</Typography>
                <div className="game-exclusions-recipient__mutual">{canEdit ? <Switch checked={Boolean(person.exclusions?.includes(card.id))}
                    label={t('gameExclusions.mutual')}
                    disabled={!canEdit || saving}
                    testId={`game-exclusion-mutual-${card.id}-${person.id}`}
                    onCheckedChange={mutual => {
                      const reverse = currentExclusions(person).map(recipient => recipient.id).filter(id => id !== card.id);
                      void saveExclusions(person, mutual ? [...reverse, card.id] : reverse);
                    }} /> : <Typography role="meta/helper">{t(person.exclusions?.includes(card.id) ? 'gameExclusions.mutual' : 'gameExclusions.oneWay')}</Typography>}</div>
                {canEdit && <Button variant="ghost" size="small" icon={<CloseIcon />}
                  aria-label={t('gameExclusions.remove', { name: person.username })}
                  disabled={saving} onClick={() => void saveExclusions(card, currentExclusions(card).map(recipient => recipient.id).filter(id => id !== person.id))} />}
              </li>
            ))}</ul>
              : <Typography role="meta/helper">{t('gameExclusions.empty')}</Typography>}

          </Stack>
        </div>}
      </Row>;
    })}</div>
    <Pagination page={currentPage} pageCount={pageCount} onChange={setPage}
      previousLabel={t('organizerPanel.exclusionsPrevious')}
      nextLabel={t('organizerPanel.exclusionsNext')}
      pageLabel={number => t('organizerPanel.exclusionsPage', { number })}
      testId="organizer-exclusions-pagination" />
    </>}
  </Stack>;
}


export function OrganizerInlineExclusions({ info, cards, onChanged }: {
  info: BoxInfo; cards: CardInfo[]; onChanged: () => Promise<void> | void;
}) {
  return <GameExclusions key={info.box.key} info={info} cards={cards} onChanged={onChanged} />;
}
